import { useEffect, useId, useMemo, useRef, useState } from 'react'

import type { EagleParam, EagleSerie } from '../api/types'
import { localeCorrente, t } from '../i18n/lingua'
import { etichettaStato, tonoStato } from './EagleStato'
import { formatConUnita, formatPreciso, scalaUnita } from './eagleTesti'

/**
 * L'andamento di uno o più parametri di Eagle nel tempo.
 *
 * SVG scritto a mano, come gli altri grafici della console (`charts.tsx`):
 * niente libreria, i colori dai token del tema (chiaro e scuro già risolti),
 * pixel veri (ResizeObserver).
 *
 * Il disegno:
 * - la **linea** del valore (media, per le finestre aggregate) con le curve
 *   **morbide** — interpolazione monotona: la curva non scavalca mai i punti,
 *   quindi non inventa picchi — e, sotto, un **velo a gradiente** che sfuma
 *   verso la base: è lui che fa leggere la forma a colpo d'occhio;
 * - a scelta (`tipo`) linea, **area**, **barre** per intervallo, serie
 *   **impilate** (la somma del traffico di più link);
 * - la **fascia minimo–massimo** a bassa opacità quando la sonda manda gli
 *   aggregati: i picchi che la media nasconde;
 * - le **soglie** come zone: la fascia fra attenzione e critico in ambra
 *   tenue, sopra il critico in rosso tenue, con la riga e il valore a
 *   margine — si vede quanto della curva sta nella zona che conta;
 * - il **valore attuale in una pillola** in coda a ogni serie, nel suo
 *   colore: l'unico numero che serve sempre; l'asse Y porta solo i numeri,
 *   l'unità sta su ogni tacca dell'asse («25 %», «400 Mbit/s»), con una
 *   scala sola per grafico che si adatta ai valori (Gbit/s, kbit/s, s, h);
 *   pillole e riquadro al passaggio del mouse usano la stessa scala;
 * - la **striscia di stato** sotto il grafico (una serie sola): ok,
 *   attenzione, critico, non risponde, minuto per minuto — un guasto si vede
 *   anche quando il valore non c'è;
 * - più **serie** — i punti di osservazione di un parametro, o i parametri
 *   scelti di un grafico personalizzato (`serieEsplicite`) — con la legenda
 *   (mai il colore da solo), fino a otto colori fissi;
 * - i **buchi** restano buchi: la curva si interrompe dove mancano letture.
 *
 * `compatto` è la sola forma, per una scheda piccola (la parete dei link):
 * velo e linea, soglie, passaggio del mouse; niente assi scritti né legenda.
 *
 * Il passaggio del mouse (o le frecce da tastiera) aggancia l'istante più
 * vicino e mostra tutte le serie in un solo riquadro.
 */

type Punto = EagleSerie['points'][number]

export interface SerieGrafico {
  id: string
  nome: string
  punti: Punto[]
}

export type TipoGrafico = 'linea' | 'area' | 'barre' | 'impilato'

const PALETTE = ['var(--serie-1)', 'var(--serie-2)', 'var(--serie-3)', 'var(--serie-4)',
  'var(--serie-5)', 'var(--serie-6)', 'var(--serie-7)', 'var(--serie-8)']

type XY = { x: number; y: number }

/**
 * Una curva morbida per i punti: interpolazione cubica **monotona**
 * (Fritsch–Carlson). Fra due letture la curva non sale mai sopra la più alta
 * né scende sotto la più bassa: è morbida perché si legge meglio, non perché
 * inventa. Con due punti è una retta.
 */
export function curva(p: XY[]): string {
  if (p.length === 0) return ''
  if (p.length < 3) return p.map((q, i) => `${i ? 'L' : 'M'} ${q.x.toFixed(1)} ${q.y.toFixed(1)}`).join(' ')
  const n = p.length
  const dx: number[] = []
  const m: number[] = []
  for (let i = 0; i < n - 1; i++) {
    const h = p[i + 1].x - p[i].x
    dx.push(h)
    m.push(h === 0 ? 0 : (p[i + 1].y - p[i].y) / h)
  }
  const tg: number[] = new Array(n).fill(0)
  tg[0] = m[0]
  tg[n - 1] = m[n - 2]
  for (let i = 1; i < n - 1; i++) tg[i] = m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) { tg[i] = 0; tg[i + 1] = 0; continue }
    const a = tg[i] / m[i]
    const b = tg[i + 1] / m[i]
    const s = a * a + b * b
    if (s > 9) {
      const tau = 3 / Math.sqrt(s)
      tg[i] = tau * a * m[i]
      tg[i + 1] = tau * b * m[i]
    }
  }
  let d = `M ${p[0].x.toFixed(1)} ${p[0].y.toFixed(1)}`
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i]
    d += ` C ${(p[i].x + h / 3).toFixed(1)} ${(p[i].y + (tg[i] * h) / 3).toFixed(1)} ${(p[i + 1].x - h / 3).toFixed(1)} ${(p[i + 1].y - (tg[i + 1] * h) / 3).toFixed(1)} ${p[i + 1].x.toFixed(1)} ${p[i + 1].y.toFixed(1)}`
  }
  return d
}

/** La stessa curva percorsa all'indietro (per chiudere fasce e pile): si costruisce sui punti invertiti. */
function curvaIndietro(p: XY[]): string {
  const d = curva([...p].reverse())
  return d.replace(/^M/, 'L')
}

/** The Y axis: from zero (percentages up to 100), fitted to the values
 *  (a floor above zero, to see small variations), or fixed limits. */
export interface ScalaY {
  modo: 'auto' | 'adatta' | 'fissa'
  min?: number | null
  max?: number | null
}

export function GraficoSerie({
  punti,
  param,
  ore,
  nomiSonde = {},
  principale,
  altezza = 200,
  tipo = 'area',
  fascia = true,
  soglie = true,
  stato = true,
  compatto = false,
  morbido = true,
  serieEsplicite,
  scalaY,
}: {
  punti: Punto[]
  param: Pick<EagleParam, 'unit' | 'warn' | 'crit' | 'direction' | 'name' | 'kind'>
  ore: number
  /** The Y scale chosen in the widget's options; absent = from zero. */
  scalaY?: ScalaY
  nomiSonde?: Record<string, string>
  principale?: string | null
  altezza?: number
  tipo?: TipoGrafico
  /** La fascia minimo–massimo (linea e area). */
  fascia?: boolean
  /** Le soglie di attenzione e critica, come zone e righe. */
  soglie?: boolean
  /** La striscia di stato sotto il grafico. */
  stato?: boolean
  /** La sola forma: niente assi scritti, legenda, striscia. */
  compatto?: boolean
  /** Curve morbide (monotone) invece di spezzate. */
  morbido?: boolean
  /** Le serie già divise (un grafico personalizzato): al posto dei `punti`. */
  serieEsplicite?: SerieGrafico[]
}) {
  const id = useId()
  const contenitore = useRef<HTMLDivElement>(null)
  const [larghezza, setLarghezza] = useState(compatto ? 240 : 720)
  const [hover, setHover] = useState<number | null>(null)
  const [puntatore, setPuntatore] = useState<{ x: number; y: number } | null>(null)

  useEffect(() => {
    const el = contenitore.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const oss = new ResizeObserver((voci) => {
      const w = voci[0]?.contentRect.width
      if (w && w > 0) setLarghezza(Math.floor(w))
    })
    oss.observe(el)
    return () => oss.disconnect()
  }, [])

  const adesso = useMemo(() => Date.now(), [punti, serieEsplicite])
  const t0 = adesso - ore * 3_600_000
  const t1 = adesso

  // Una serie per punto di osservazione (le misure senza sonda — di prima
  // dei punti, o dall'agente — vanno con il principale), oppure le serie
  // esplicite del grafico personalizzato.
  const serie: SerieGrafico[] = useMemo(() => {
    const dentro = (lista: Punto[]) => lista
      .filter((p) => { const ts = new Date(p.ts).getTime(); return ts >= t0 && !Number.isNaN(ts) })
      .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime())
    if (serieEsplicite) return serieEsplicite.slice(0, PALETTE.length).map((s) => ({ ...s, punti: dentro(s.punti) }))
    const per = new Map<string, Punto[]>()
    for (const p of punti) {
      const ts = new Date(p.ts).getTime()
      if (ts < t0 || Number.isNaN(ts)) continue
      const chiave = p.appliance_id || principale || 'ci'
      const lista = per.get(chiave) ?? []
      lista.push(p)
      per.set(chiave, lista)
    }
    const ordinate = [...per.entries()].sort(([a], [b]) => (a === principale ? -1 : b === principale ? 1 : a.localeCompare(b)))
    return ordinate.slice(0, PALETTE.length).map(([sid, lista]) => ({
      id: sid,
      nome: nomiSonde[sid] ?? (sid === 'ci' ? '' : sid.slice(0, 8)),
      punti: dentro(lista),
    }))
  }, [punti, serieEsplicite, t0, principale, nomiSonde])

  // Il riferimento (agganci del mouse, striscia di stato): la serie con più letture.
  const riferimento = serie.reduce<SerieGrafico | null>((best, s) => (!best || s.punti.length > best.punti.length ? s : best), null)
  const tutti = useMemo(() => serie.flatMap((s) => s.punti), [serie])

  if (!riferimento || tutti.filter((p) => p.value != null).length < 2) {
    return (
      <div ref={contenitore} className={`gserie${compatto ? ' gserie--compatto' : ''}`}>
        <p className="muted" style={{ margin: compatto ? '8px 0' : '16px 0', fontSize: compatto ? 11 : undefined }}>{t('Servono almeno due misure per disegnare l’andamento.')}</p>
      </div>
    )
  }

  // --- Le scale
  const passo = passoTipico(riferimento.punti)
  const chiaveTs = (ts: number) => Math.round(ts / passo) * passo

  // Le serie impilate: il valore di ognuna sopra la somma delle precedenti,
  // allineate all'intervallo tipico; dove una manca conta zero.
  const mappe = serie.map((s) => {
    const m = new Map<number, number>()
    for (const p of s.punti) if (p.value != null) m.set(chiaveTs(new Date(p.ts).getTime()), p.value)
    return m
  })
  const tuttiTs = [...new Set(mappe.flatMap((m) => [...m.keys()]))].sort((a, b) => a - b)
  const cumulate: Map<number, number>[] = []
  if (tipo === 'impilato') {
    let prec = new Map<number, number>()
    for (const m of mappe) {
      const cur = new Map<number, number>()
      for (const ts of tuttiTs) cur.set(ts, (prec.get(ts) ?? 0) + (m.get(ts) ?? 0))
      cumulate.push(cur)
      prec = cur
    }
  }
  const valori = tutti.flatMap((p) => [p.value, p.max, p.min]).filter((v): v is number => v != null)
  const massimoDati = tipo === 'impilato' && cumulate.length
    ? Math.max(1, ...cumulate[cumulate.length - 1].values())
    : Math.max(1, ...valori)
  const soglieValori = soglie ? [param.warn, param.crit].filter((v): v is number => v != null) : []
  // The scale: from zero (the percentages up to 100), fitted to the values
  // with a floor above zero (a CPU at 2–5 %, a temperature at 41–46 °C, a
  // ping at 10–13 ms stop being a flat line), or the limits the user wrote.
  const modo = tipo === 'impilato' ? 'auto' : (scalaY?.modo ?? 'auto')
  const piuAlto = Math.max(massimoDati, ...soglieValori)
  let massimo = niceMax(piuAlto)
  let minimo = 0
  if (modo === 'auto' && param.unit === '%' && massimo <= 100 && tipo !== 'impilato') massimo = 100
  if (modo === 'adatta') {
    const piuBasso = Math.min(...valori)
    if (Number.isFinite(piuBasso)) {
      minimo = nicePiano(piuBasso, piuAlto)
      massimo = niceSoffitto(piuAlto, minimo)
    }
  } else if (modo === 'fissa') {
    if (scalaY?.max != null && Number.isFinite(scalaY.max)) massimo = scalaY.max
    if (scalaY?.min != null && Number.isFinite(scalaY.min)) minimo = scalaY.min
    if (!(massimo > minimo)) { minimo = 0; massimo = niceMax(piuAlto) }
  }
  const ampiezza = massimo - minimo
  const dentro = (v: number) => v > minimo && v < massimo
  const dentroInc = (v: number) => v >= minimo && v <= massimo
  // L'unità sta su ogni tacca dell'asse (tranne lo zero): «25 %», «400
  // Mbit/s» — una piccola parola in un angolo non si vedeva, e un asse di
  // soli numeri non dice che cosa misura. La scala dell'asse si sceglie sul
  // massimo (oltre 1000 Mbit/s si legge in Gbit/s, con i decimali che
  // servono: «0.25 Gbit/s», non «0.3»); i valori singoli — pillole, soglie,
  // riquadro al passaggio del mouse — si scalano ognuno per sé, così una
  // serie da 98 Mbit/s resta «98.5 Mbit/s» anche sotto un asse in Gbit/s.
  const scala = scalaUnita(param.unit ?? '', massimo)
  const etichettaTacca = (v: number) => {
    const n = formatPreciso(v / scala.fattore)
    return v === 0 || !scala.unit ? n : `${n} ${scala.unit}`
  }
  const conUnita = (v: number) => formatConUnita(v, param.unit)
  const unita = (v: number | null | undefined) => (v == null ? '—' : conUnita(v))
  const tacche = compatto ? [] : [0, 0.25, 0.5, 0.75, 1].map((f) => minimo + f * ampiezza)
  // I margini seguono le etichette più lunghe: a sinistra la tacca («400
  // Mbit/s»), a destra la pillola in coda («98.5 Mbit/s»), così niente viene
  // tagliato e un'etichetta mozza non diventa un numero sbagliato.
  const piuLunga = Math.max(0, ...tacche.map((v) => etichettaTacca(v).length))
  const codaPiuLunga = Math.max(0, ...serie.map((s) => {
    const ultimo = [...s.punti].reverse().find((p) => p.value != null)
    return ultimo ? conUnita(ultimo.value as number).length : 0
  }))
  const M = compatto
    ? { top: 6, right: 8, bottom: 6, left: 6 }
    // Con la striscia di stato il margine deve contenere anche la parola «stato».
    : { top: 22, right: Math.max(64, 24 + codaPiuLunga * 6.8), bottom: stato && serie.length === 1 ? 44 : 26, left: Math.min(104, Math.max(stato && serie.length === 1 ? 48 : 34, 14 + piuLunga * 6.4)) }
  const W = Math.max(compatto ? 120 : 320, larghezza)
  const H = altezza
  const plotW = W - M.left - M.right
  const plotH = H - M.top - M.bottom
  const x = (ts: number) => M.left + ((ts - t0) / Math.max(1, t1 - t0)) * plotW
  const y = (v: number) => M.top + plotH - ((Math.min(Math.max(v, minimo), massimo) - minimo) / ampiezza) * plotH
  const base = M.top + plotH
  const tempi = compatto ? [] : taccheTempo(t0, t1, ore)
  const slot = Math.max(2, plotW * (passo / Math.max(1, t1 - t0)) * 0.8)
  const slotSerie = Math.max(1, slot / serie.length)
  const traccia = (p: XY[]) => (morbido ? curva(p) : p.map((q, i) => `${i ? 'L' : 'M'} ${q.x.toFixed(1)} ${q.y.toFixed(1)}`).join(' '))
  const tracciaIndietro = (p: XY[]) => (morbido ? curvaIndietro(p) : [...p].reverse().map((q) => `L ${q.x.toFixed(1)} ${q.y.toFixed(1)}`).join(' '))

  // --- Le forme, per serie: curva e fascia interrotte dove mancano letture;
  // il velo chiuso sulla base; barre per intervallo; pila fra due cumulate.
  const forme = serie.map((s, i) => {
    const tratti: Punto[][] = []
    let corrente: Punto[] = []
    let prima = 0
    for (const p of s.punti) {
      const ts = new Date(p.ts).getTime()
      // Un buco nelle letture, o una lettura senza valore (un ping che non
      // risponde): la curva si interrompe, non si tira dritta sopra il vuoto.
      if (corrente.length && (ts - prima > passo * 3 || p.value == null)) {
        tratti.push(corrente)
        corrente = []
      }
      if (p.value != null) corrente.push(p)
      prima = ts
    }
    if (corrente.length) tratti.push(corrente)
    const xy = (tr: Punto[], prendi: (p: Punto) => number) => tr.map((p) => ({ x: x(new Date(p.ts).getTime()), y: y(prendi(p)) }))
    const linea = tratti.map((tr) => traccia(xy(tr, (p) => p.value as number))).join(' ')
    const area = tratti
      .map((tr) => {
        const sopra = xy(tr, (p) => p.value as number)
        return `${traccia(sopra)} L ${sopra[sopra.length - 1].x.toFixed(1)} ${base.toFixed(1)} L ${sopra[0].x.toFixed(1)} ${base.toFixed(1)} Z`
      })
      .join(' ')
    const fasciaPath = tratti
      .filter((tr) => tr.some((p) => p.min != null && p.max != null))
      .map((tr) => `${traccia(xy(tr, (p) => p.max ?? (p.value as number)))} ${tracciaIndietro(xy(tr, (p) => p.min ?? (p.value as number)))} Z`)
      .join(' ')
    const barre = tipo === 'barre'
      ? s.punti.filter((p) => p.value != null).map((p) => {
        const cx = x(new Date(p.ts).getTime())
        const top = y(p.value as number)
        return { x: cx - slot / 2 + i * slotSerie, y: top, w: slotSerie, h: Math.max(0, base - top) }
      })
      : []
    let pila = ''
    let lineaPila = ''
    if (tipo === 'impilato' && tuttiTs.length) {
      const sopra = cumulate[i]
      const sotto = i > 0 ? cumulate[i - 1] : null
      const su = tuttiTs.map((ts) => ({ x: x(ts), y: y(sopra.get(ts) ?? 0) }))
      const giu = tuttiTs.map((ts) => ({ x: x(ts), y: sotto ? y(sotto.get(ts) ?? 0) : base }))
      pila = `${traccia(su)} ${tracciaIndietro(giu)} Z`
      lineaPila = traccia(su)
    }
    // L'ultimo valore della serie, da scrivere nella pillola in coda: il suo,
    // anche nell'impilato (la pillola sta sul bordo superiore della sua fetta;
    // il totale è in legenda e nel riquadro al passaggio del mouse).
    const ultimo = [...s.punti].reverse().find((p) => p.value != null) ?? null
    const yCoda = ultimo
      ? (tipo === 'impilato' ? y(cumulate[i].get(chiaveTs(new Date(ultimo.ts).getTime())) ?? (ultimo.value as number)) : y(ultimo.value as number))
      : 0
    return {
      serie: s, colore: serie.length > 1 ? PALETTE[i] : 'var(--accent)', linea, area, fascia: fasciaPath, barre, pila, lineaPila,
      coda: ultimo && ultimo.value != null ? { x: x(new Date(ultimo.ts).getTime()), y: yCoda, testo: conUnita(ultimo.value) } : null,
    }
  })
  const totaleCoda = tipo === 'impilato' && cumulate.length && tuttiTs.length
    ? cumulate[cumulate.length - 1].get(tuttiTs[tuttiTs.length - 1]) ?? null : null

  // Le pillole in coda non si sovrappongono: dall'alto in basso, almeno 16px l'una dall'altra.
  const pillole = forme
    .map((f, i) => (f.coda ? { i, ...f.coda } : null))
    .filter((p): p is { i: number; x: number; y: number; testo: string } => !!p)
    .sort((a, b) => a.y - b.y)
  for (let k = 1; k < pillole.length; k++) if (pillole[k].y < pillole[k - 1].y + 16) pillole[k].y = pillole[k - 1].y + 16

  // --- La striscia di stato (dal riferimento, una serie sola): tratti contigui dello stesso stato.
  const striscia: { da: number; a: number; stato: string }[] = []
  if (stato && !compatto && serie.length === 1) {
    for (let i = 0; i < riferimento.punti.length; i++) {
      const p = riferimento.punti[i]
      const da = new Date(p.ts).getTime()
      const prossimo = riferimento.punti[i + 1]
      const a = Math.min(t1, prossimo ? new Date(prossimo.ts).getTime() : da + passo)
      const ultimo = striscia[striscia.length - 1]
      if (ultimo && ultimo.stato === p.status && da - ultimo.a <= passo * 3) ultimo.a = a
      else striscia.push({ da, a, stato: p.status })
    }
  }

  // --- Il passaggio del mouse: l'istante più vicino sul riferimento.
  const agganciaX = (px: number) => {
    const ts = t0 + ((px - M.left) / plotW) * (t1 - t0)
    let migliore = 0
    let distanza = Infinity
    riferimento.punti.forEach((p, i) => {
      const d = Math.abs(new Date(p.ts).getTime() - ts)
      if (d < distanza) {
        distanza = d
        migliore = i
      }
    })
    return migliore
  }
  const inHover = hover != null ? riferimento.punti[hover] : null
  const tsHover = inHover ? new Date(inHover.ts).getTime() : null
  const righeTooltip = tsHover == null ? [] : forme.map((f) => {
    let vicino: Punto | null = null
    let distanza = Infinity
    for (const p of f.serie.punti) {
      const d = Math.abs(new Date(p.ts).getTime() - tsHover)
      if (d < distanza) {
        distanza = d
        vicino = p
      }
    }
    return { forma: f, punto: distanza <= passo * 1.5 ? vicino : null }
  })
  const conFascia = fascia && (tipo === 'linea' || tipo === 'area') && tutti.some((p) => p.min != null && p.max != null)
  const conZone = soglie && !compatto && (param.warn != null || param.crit != null)
  const sopraSoglia = param.direction !== 'below'

  const tooltipX = puntatore ? Math.min(Math.max(puntatore.x + 14, 0), Math.max(0, W - 230)) : 0
  const tooltipY = puntatore ? Math.max(0, puntatore.y - 12) : 0

  return (
    <div ref={contenitore} className={`gserie${compatto ? ' gserie--compatto' : ''}`}>
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        className="gs__svg"
        role="img"
        tabIndex={compatto ? undefined : 0}
        aria-label={t('Andamento di {parametro} nelle ultime {ore} ore', { parametro: param.name, ore })}
        onMouseMove={(e) => {
          const box = e.currentTarget.getBoundingClientRect()
          const px = e.clientX - box.left
          const py = e.clientY - box.top
          if (px < M.left || px > W - M.right) {
            setHover(null)
            return
          }
          setHover(agganciaX(px))
          setPuntatore({ x: px, y: py })
        }}
        onMouseLeave={() => setHover(null)}
        onKeyDown={(e) => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
          e.preventDefault()
          const n = riferimento.punti.length
          const prossimo = hover == null ? n - 1 : Math.max(0, Math.min(n - 1, hover + (e.key === 'ArrowRight' ? 1 : -1)))
          setHover(prossimo)
          setPuntatore({ x: x(new Date(riferimento.punti[prossimo].ts).getTime()), y: M.top })
        }}
      >
        <defs>
          <clipPath id={`${id}-clip`}>
            <rect x={M.left} y={M.top} width={plotW} height={plotH} />
          </clipPath>
          {/* Un velo a gradiente per serie: pieno in alto, trasparente alla base. */}
          {forme.map((f, i) => (
            <linearGradient key={f.serie.id} id={`${id}-velo-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={f.colore} stopOpacity={tipo === 'area' ? (serie.length > 1 ? 0.22 : 0.38) : 0.14} />
              <stop offset="100%" stopColor={f.colore} stopOpacity={0.02} />
            </linearGradient>
          ))}
        </defs>

        {/* Le zone di soglia: fra attenzione e critico in ambra tenue, oltre il critico in rosso tenue. */}
        {conZone && sopraSoglia && param.warn != null && dentro(param.warn) && (
          <rect x={M.left} width={plotW} y={y(Math.min(massimo, param.crit ?? massimo))} height={Math.max(0, y(param.warn) - y(Math.min(massimo, param.crit ?? massimo)))} className="gs__zona gs__zona--warn" />
        )}
        {conZone && sopraSoglia && param.crit != null && dentro(param.crit) && (
          <rect x={M.left} width={plotW} y={M.top} height={Math.max(0, y(param.crit) - M.top)} className="gs__zona gs__zona--crit" />
        )}
        {conZone && !sopraSoglia && param.warn != null && dentroInc(param.warn) && (
          <rect x={M.left} width={plotW} y={y(param.warn)} height={Math.max(0, y(Math.max(0, param.crit ?? 0)) - y(param.warn))} className="gs__zona gs__zona--warn" />
        )}
        {conZone && !sopraSoglia && param.crit != null && dentroInc(param.crit) && (
          <rect x={M.left} width={plotW} y={y(param.crit)} height={Math.max(0, base - y(param.crit))} className="gs__zona gs__zona--crit" />
        )}

        {/* Griglia: orizzontale, sottile, in secondo piano; la base un filo più decisa. */}
        {tacche.map((v) => (
          <g key={v}>
            <line x1={M.left} x2={W - M.right} y1={y(v)} y2={y(v)} className={v === 0 ? 'gs__base' : 'gs__grid'} />
            <text x={M.left - 8} y={y(v) + 4} textAnchor="end" className="gs__tick">{etichettaTacca(v)}</text>
          </g>
        ))}
        {compatto && <line x1={M.left} x2={W - M.right} y1={base} y2={base} className="gs__base" />}
        {tempi.map((tt) => (
          <g key={tt.ts}>
            <line x1={x(tt.ts)} x2={x(tt.ts)} y1={base} y2={base + 4} className="gs__grid" />
            <text x={x(tt.ts)} y={base + 16} textAnchor="middle" className="gs__tick">{tt.label}</text>
          </g>
        ))}

        {/* Le righe di soglia, con il valore a margine. */}
        {soglie && param.warn != null && dentroInc(param.warn) && (
          <g>
            <line x1={M.left} x2={W - M.right} y1={y(param.warn)} y2={y(param.warn)} className="gs__soglia gs__soglia--warn" />
            {!compatto && <text x={W - M.right + 6} y={y(param.warn) + 4} className="gs__tick gs__tick--warn">{conUnita(param.warn)}</text>}
          </g>
        )}
        {soglie && param.crit != null && dentroInc(param.crit) && (
          <g>
            <line x1={M.left} x2={W - M.right} y1={y(param.crit)} y2={y(param.crit)} className="gs__soglia gs__soglia--crit" />
            {!compatto && <text x={W - M.right + 6} y={y(param.crit) + 4} className="gs__tick gs__tick--crit">{conUnita(param.crit)}</text>}
          </g>
        )}

        <g clipPath={`url(#${id}-clip)`}>
          {forme.map((f, i) => (
            <g key={f.serie.id}>
              {tipo === 'impilato' && f.pila && <path d={f.pila} fill={f.colore} className="gs__pila" />}
              {(tipo === 'area' || tipo === 'linea') && f.area && <path d={f.area} fill={`url(#${id}-velo-${i})`} className="gs__velo" />}
              {tipo === 'barre' && f.barre.map((b, j) => (
                <rect key={j} x={b.x} y={b.y} width={b.w} height={b.h} rx={Math.min(2, b.w / 2)} fill={f.colore} className="gs__colonna" />
              ))}
              {conFascia && f.fascia && <path d={f.fascia} fill={f.colore} className="gs__fascia" />}
              {tipo === 'impilato' && f.lineaPila && <path d={f.lineaPila} stroke={f.colore} className="gs__linea" />}
              {(tipo === 'linea' || tipo === 'area') && <path d={f.linea} stroke={f.colore} className="gs__linea" />}
            </g>
          ))}
        </g>

        {/* Il valore attuale, in una pillola in coda a ogni serie. */}
        {!compatto && pillole.map((p) => {
          const f = forme[p.i]
          const larg = 10 + p.testo.length * 6.8
          return (
            <g key={f.serie.id} className="gs__coda">
              <circle cx={p.x} cy={f.coda!.y} r={3.5} fill={f.colore} className="gs__punto" />
              <rect x={p.x + 7} y={p.y - 9} width={larg} height={18} rx={9} fill={f.colore} />
              <text x={p.x + 7 + larg / 2} y={p.y + 4} textAnchor="middle" className="gs__pillola">{p.testo}</text>
            </g>
          )
        })}

        {/* La striscia di stato. */}
        {striscia.length > 0 && (
          <g>
            {striscia.map((s, i) => (
              <rect
                key={i}
                x={x(s.da)}
                y={base + 24}
                width={Math.max(1, x(s.a) - x(s.da))}
                height={5}
                rx={2}
                className={`gs__stato gs__stato--${tonoStato(s.stato)}`}
              >
                <title>{`${etichettaStato(s.stato)} · ${formatOra(s.da)} – ${formatOra(s.a)}`}</title>
              </rect>
            ))}
            <text x={M.left - 8} y={base + 30} textAnchor="end" className="gs__tick">{t('stato')}</text>
          </g>
        )}

        {hover != null && tsHover != null && (
          <g>
            <line x1={x(tsHover)} x2={x(tsHover)} y1={M.top} y2={base} className="gs__cross" />
            {tipo !== 'barre' && tipo !== 'impilato' && righeTooltip.map(({ forma, punto }) =>
              punto?.value != null ? (
                <circle key={forma.serie.id} cx={x(new Date(punto.ts).getTime())} cy={y(punto.value)} r={4.5} fill="var(--surface)" stroke={forma.colore} className="gs__mirino" />
              ) : null,
            )}
          </g>
        )}
      </svg>

      {hover != null && inHover && puntatore && (
        <div className="gs__tooltip" style={{ left: tooltipX, top: tooltipY }} role="status">
          <strong>{formatIstante(inHover.ts)}</strong>
          {righeTooltip.map(({ forma, punto }) => (
            <div key={forma.serie.id} className="gs__riga">
              <span className="gs__chiave" style={{ background: forma.colore }} />
              {forma.serie.nome && <span className="muted">{forma.serie.nome}</span>}
              <strong>{unita(punto?.value)}</strong>
              {punto && !serieEsplicite && (
                <span className={`badge badge--${tonoStato(punto.status)}`}>{etichettaStato(punto.status)}</span>
              )}
              {punto && punto.min != null && punto.max != null && (
                <span className="muted">{t('min {min} · max {max}', { min: unita(punto.min), max: unita(punto.max) })}</span>
              )}
              {punto && punto.p95 != null && <span className="muted">{t('95° pct {v}', { v: unita(punto.p95) })}</span>}
            </div>
          ))}
          {tipo === 'impilato' && tsHover != null && cumulate.length > 0 && (
            <div className="gs__riga"><span className="muted">{t('totale')}</span><strong>{unita(cumulate[cumulate.length - 1].get(chiaveTs(tsHover)) ?? null)}</strong></div>
          )}
        </div>
      )}

      {!compatto && (serie.length > 1 || conFascia) && (
        <div className="gs__legenda">
          {serie.length > 1 &&
            forme.map((f) => (
              <span key={f.serie.id} className="gs__voce gs__voce--pillola">
                <span className="gs__chiave" style={{ background: f.colore }} />
                {f.serie.nome || t('CI')}
                {f.coda && <strong className="mono">{f.coda.testo}</strong>}
              </span>
            ))}
          {totaleCoda != null && <span className="gs__voce gs__voce--pillola"><span className="muted">{t('totale')}</span><strong className="mono">{unita(totaleCoda)}</strong></span>}
          {conFascia && <span className="muted">{t('la fascia è il minimo–massimo di ogni intervallo; la linea la media')}</span>}
        </div>
      )}
    </div>
  )
}

/** Un minimo «pulito» sotto il valore più basso, a passi coerenti con l'ampiezza. */
function nicePiano(piuBasso: number, piuAlto: number): number {
  if (piuBasso <= 0) return piuBasso < 0 ? -niceMax(-piuBasso) : 0
  const passo = niceMax(Math.max(piuAlto - piuBasso, piuAlto * 0.02, 1e-9) / 4)
  return Math.max(0, Math.floor(piuBasso / passo) * passo)
}

/** Un massimo «pulito» sopra il valore più alto, a passi coerenti con l'ampiezza. */
function niceSoffitto(piuAlto: number, minimo: number): number {
  const passo = niceMax(Math.max(piuAlto - minimo, 1e-9) / 4)
  const tetto = Math.ceil(piuAlto / passo) * passo
  return tetto > minimo ? tetto : minimo + passo
}

/** Un massimo «pulito» per l'asse: 1, 2, 5 × 10ⁿ. */
function niceMax(v: number): number {
  if (v <= 0) return 1
  const ordine = 10 ** Math.floor(Math.log10(v))
  const n = v / ordine
  const passo = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10
  return passo * ordine
}

/** L'intervallo tipico fra due letture (la mediana): serve a vedere i buchi. */
function passoTipico(punti: Punto[]): number {
  const diff: number[] = []
  for (let i = 1; i < punti.length; i++) {
    diff.push(new Date(punti[i].ts).getTime() - new Date(punti[i - 1].ts).getTime())
  }
  if (!diff.length) return 60_000
  diff.sort((a, b) => a - b)
  return Math.max(30_000, diff[Math.floor(diff.length / 2)])
}

/** Le tacche dei tempi: poche, a ore tonde. */
function taccheTempo(t0: number, t1: number, ore: number): { ts: number; label: string }[] {
  const passo = ore <= 0.5 ? 5 * 60_000 : ore <= 1 ? 10 * 60_000 : ore <= 3 ? 30 * 60_000 : ore <= 6 ? 3_600_000 : ore <= 24 ? 3 * 3_600_000 : ore <= 48 ? 6 * 3_600_000 : 24 * 3_600_000
  const out: { ts: number; label: string }[] = []
  const prima = new Date(t0)
  // Allineate al passo nell'ora locale: «15:00», non «14:37».
  prima.setSeconds(0, 0)
  const minuti = passo / 60_000
  if (passo >= 3_600_000) prima.setMinutes(0)
  else prima.setMinutes(Math.ceil(prima.getMinutes() / minuti) * minuti)
  if (passo >= 24 * 3_600_000) prima.setHours(0)
  else if (passo > 3_600_000) prima.setHours(Math.ceil(prima.getHours() / (passo / 3_600_000)) * (passo / 3_600_000))
  for (let ts = prima.getTime(); ts <= t1; ts += passo) {
    if (ts < t0) continue
    const d = new Date(ts)
    out.push({
      ts,
      label: passo >= 24 * 3_600_000
        ? d.toLocaleDateString(localeCorrente(), { day: '2-digit', month: '2-digit' })
        : d.toLocaleTimeString(localeCorrente(), { hour: '2-digit', minute: '2-digit' }),
    })
  }
  return out
}

function formatOra(ts: number): string {
  return new Date(ts).toLocaleTimeString(localeCorrente(), { hour: '2-digit', minute: '2-digit' })
}

function formatIstante(iso: string): string {
  return new Date(iso).toLocaleString(localeCorrente(), { dateStyle: 'short', timeStyle: 'short' })
}
